import { useTheme } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useEffect } from "react";

import { Stack } from "@/Components/v2/atoms/Stack";
import { useConfig } from "@/hooks/useConfig";
import { MROIColumnSelector } from "@/pages/channel-roi/constants/types";
import { useMROIStore } from "@/pages/channel-roi/store/useMROIStore";
import { AggregatedData } from "@/pages/channel-roi/types";
import ConditionalScroll from "@/shared/components/ConditionalScroll";

import { getChartOptions } from "./chartOptions";
import ColumnChartViewSelector from "./ColumnChartViewSelector";
import { isMarketingReturn, isShareOfEffect, isSpendRevenueROI } from "./helpers";
import { LegendOptions } from "./hooks/useColumnChartLegends";
import Legend, { LegendItem } from "./Legend";

type ColumnChartProps = {
  nodes: AggregatedData[];
  hasSharedRevenue: boolean;
  hasRoiRevenue: boolean;
  legendItems: LegendItem[];
  legendOptions: LegendOptions;
  height?: string;
  maxHeight?: string;
};

const ColumnChart = ({
  nodes,
  hasSharedRevenue,
  hasRoiRevenue,
  legendItems,
  legendOptions,
  height = "60vh",
  maxHeight = "700px",
}: ColumnChartProps) => {
  const columnView = useMROIStore((state) => state.chart.channelEffectiveness.column.selector);
  const funnelStage = useMROIStore((state) => state.filters.funnelStage);
  const { setColumnSelector } = useMROIStore();

  const { config } = useConfig();
  const theme = useTheme();

  const chartNodes = nodes;
  const hasRoiNodes = !!nodes.length;

  // Setting the appropriate column view – only if we are outside the view set to maintain the selected view.
  useEffect(() => {
    if (hasSharedRevenue && funnelStage === "all") {
      !isMarketingReturn(columnView) &&
        setColumnSelector(MROIColumnSelector.MARKETING_RETURN_AMOUNT);
    } else if (hasSharedRevenue) {
      !isShareOfEffect(columnView) && setColumnSelector(MROIColumnSelector.SHARE_OF_EFFECT_AMOUNT);
    } else if (hasRoiNodes) {
      !isSpendRevenueROI(columnView) &&
        setColumnSelector(MROIColumnSelector.SPEND_VS_REVENUE_AMOUNT);
    }
  }, [hasSharedRevenue, hasRoiNodes, funnelStage]);

  const options = getChartOptions(
    columnView,
    chartNodes,
    config,
    theme,
    legendOptions,
    funnelStage ?? ""
  );

  const PX_PER_COLUMN = 120;
  const SCROLL_THRESHOLD = 9;
  return columnView ? (
    <>
      <Stack direction="row" justifyContent="flex-end" mb={2}>
        <ColumnChartViewSelector
          config={config}
          hasSharedRevenue={hasSharedRevenue}
          hasRoiRevenue={hasRoiRevenue}
        />
      </Stack>
      <ConditionalScroll
        unitWidthPx={PX_PER_COLUMN}
        scrollThreshold={SCROLL_THRESHOLD}
        numberOfItems={chartNodes.length}
      >
        <ReactECharts
          key={`${columnView}-${JSON.stringify(legendOptions)}`}
          option={options}
          style={{ height, maxHeight }}
        />
      </ConditionalScroll>
      <Stack direction="row" spacing={2} my={2} justifyContent="center">
        <Legend theme={theme} legends={legendItems} />
      </Stack>
    </>
  ) : null;
};

export default ColumnChart;
